import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CalendarButton.css';

/* ─────────────────────────────────────────────────────────────────
   CalendarButton — кнопка «Добавить в календарь» с выбором календаря.

   По клику открывается меню: Google, Outlook и «Apple, Яндекс и другие»
   (скачивание .ics — системная функция: ОС сама предложит открыть в
   Календаре / выбрать приложение). Меню рендерится порталом в body со
   своими стилями, поэтому одинаково работает в любом шаблоне.
   ───────────────────────────────────────────────────────────────── */

const pad = n => String(n).padStart(2, '0');

/* Событие строим в ПЛАВАЮЩЕМ локальном времени (без UTC/Z), чтобы в
   календаре гостя отображалось ровно то время, что указано на сайте,
   а не «уезжало» на часовой пояс. Если время не задано — событие на
   весь день. `date` = "YYYY-MM-DD", `time` = "HH:MM". */
/* Место проведения для календаря: первый заполненный адрес из venues
   («Название, Адрес»), иначе — общий wedding_address. */
function eventLocation(venues, address) {
  const v = (venues || []).find(x => x && String(x.address || '').trim());
  if (v) return [v.name, v.address].map(s => String(s || '').trim()).filter(Boolean).join(', ');
  return String(address || '').trim();
}

function buildEvent({ bride = '', groom = '', date, time, venues, address, durationHours = 6 }) {
  if (!date) return null;
  const dm = String(date).match(/(\d{4})-(\d{2})-(\d{2})/);
  let Y, M, D;
  if (dm) { Y = +dm[1]; M = +dm[2]; D = +dm[3]; }
  else {
    const d0 = new Date(date);
    if (isNaN(d0.getTime())) return null;
    Y = d0.getFullYear(); M = d0.getMonth() + 1; D = d0.getDate();
  }
  const tm = String(time || '').match(/(\d{1,2}):(\d{2})/);
  const allDay = !tm;
  const h = tm ? Math.min(23, +tm[1]) : 0;
  const min = tm ? Math.min(59, +tm[2]) : 0;
  const start = new Date(Y, M - 1, D, h, min, 0, 0);
  const end = allDay
    ? new Date(Y, M - 1, D + 1)                                  // весь день → следующий день
    : new Date(start.getTime() + durationHours * 3600 * 1000);
  const title = `Свадьба ${bride} и ${groom}`.replace(/\s+/g, ' ').trim();
  return { title, allDay, start, end, location: eventLocation(venues, address) };
}

/* Плавающее локальное время (без Z): дата и дата-время. */
const fLocalDate = d => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
const fLocalDT   = d => `${fLocalDate(d)}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
/* UTC-момент со «Z» — только для DTSTAMP. */
const fUtc = d => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;

function googleUrl(ev) {
  const dates = ev.allDay
    ? `${fLocalDate(ev.start)}/${fLocalDate(ev.end)}`
    : `${fLocalDT(ev.start)}/${fLocalDT(ev.end)}`;
  return 'https://calendar.google.com/calendar/render?action=TEMPLATE'
    + `&text=${encodeURIComponent(ev.title)}`
    + `&dates=${dates}`
    + (ev.location ? `&location=${encodeURIComponent(ev.location)}` : '');
}

function outlookUrl(ev) {
  const iso = d => ev.allDay
    ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  return 'https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent'
    + `&subject=${encodeURIComponent(ev.title)}`
    + `&startdt=${iso(ev.start)}`
    + `&enddt=${iso(ev.end)}`
    + (ev.location ? `&location=${encodeURIComponent(ev.location)}` : '')
    + (ev.allDay ? '&allday=true' : '');
}

/* Скачать .ics — универсально: Apple Календарь, Яндекс Календарь (импорт),
   Android/Windows. На мобильных ОС сама предложит выбрать приложение. */
function downloadIcs(ev) {
  const esc = s => String(s).replace(/([,;\\])/g, '\\$1').replace(/\r?\n/g, '\\n');
  const startLine = ev.allDay ? `DTSTART;VALUE=DATE:${fLocalDate(ev.start)}` : `DTSTART:${fLocalDT(ev.start)}`;
  const endLine   = ev.allDay ? `DTEND;VALUE=DATE:${fLocalDate(ev.end)}`     : `DTEND:${fLocalDT(ev.end)}`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Amorika//Wedding//RU',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${fLocalDT(ev.start)}-amorika@amorika.love`,
    `DTSTAMP:${fUtc(new Date())}`,
    startLine,
    endLine,
    `SUMMARY:${esc(ev.title)}`,
  ];
  if (ev.location) lines.push(`LOCATION:${esc(ev.location)}`);
  /* Напоминание за сутки (VALARM). Google/Outlook через URL напоминание
     не поддерживают — там сработают дефолтные настройки пользователя. */
  lines.push(
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'DESCRIPTION:Напоминание о свадьбе',
    'TRIGGER:-P1D',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  );
  const ics = lines.join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'svadba.ics';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/* Обратная совместимость: прямое открытие Google (если где-то импортируют). */
export function openGoogleCalendar(opts = {}) {
  const ev = buildEvent(opts);
  if (!ev) return false;
  window.open(googleUrl(ev), '_blank', 'noopener');
  return true;
}

const ITEMS = [
  { key: 'ics',     label: 'Apple, Android, Windows, Яндекс', icon: '🗓️', hint: 'файл .ics', run: ev => downloadIcs(ev) },
  { key: 'google',  label: 'Google Календарь', icon: '📅', run: ev => window.open(googleUrl(ev), '_blank', 'noopener') },
  { key: 'outlook', label: 'Outlook',          icon: '📨', run: ev => window.open(outlookUrl(ev), '_blank', 'noopener') },
];

export default function CalendarButton({ bride, groom, date, time, venues, address, durationHours = 6, className, style, children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef(null);
  const ev = buildEvent({ bride, groom, date, time, venues, address, durationHours });

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    const onDoc = e => { if (btnRef.current && !btnRef.current.contains(e.target) && !e.target.closest('.cal-menu')) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onDoc);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onDoc);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    if (!ev) return;
    const r = btnRef.current.getBoundingClientRect();
    const width = Math.max(240, Math.min(r.width, window.innerWidth - 16));
    let left = r.left + r.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    /* Оценка высоты меню (заголовок + пункты). Если снизу не хватает
       места — раскрываем вверх; в любом случае кламп в пределах экрана. */
    const estH = 52 + ITEMS.length * 52;
    const spaceBelow = window.innerHeight - r.bottom;
    let top = (spaceBelow < estH + 12 && r.top > estH + 12) ? r.top - estH - 8 : r.bottom + 8;
    top = Math.max(8, Math.min(top, window.innerHeight - estH - 8));
    setPos({ top, left, width });
    setOpen(o => !o);
  };

  const pick = item => { item.run(ev); setOpen(false); };

  return (
    <>
      <button type="button" ref={btnRef} className={className} style={style}
        data-cb-section="calendar" aria-haspopup="menu" aria-expanded={open}
        onClick={toggle}>
        {children || 'Добавить в календарь'}
      </button>

      {open && ev && createPortal(
        <div className="cal-menu" role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 100000 }}>
          <div className="cal-menu-title">Выберите календарь</div>
          {ITEMS.map(it => (
            <button key={it.key} type="button" role="menuitem" className="cal-menu-item" onClick={() => pick(it)}>
              <span className="cal-menu-ic" aria-hidden="true">{it.icon}</span>
              <span className="cal-menu-lbl">{it.label}{it.hint && <em className="cal-menu-hint"> · {it.hint}</em>}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
